import React, { CSSProperties, useEffect, useMemo, useState } from 'react';
import { CrownOutlined, SwapOutlined, TrophyOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import EmptyState from '../components/EmptyState';
import FranchiseLogo from '../components/FranchiseLogo';
import LoadingSkeleton from '../components/LoadingSkeleton';
import { useFranchises } from '../hooks/useFantasyData';
import { useAppDispatch } from '../store/hooks';
import { clearSelectedFranchise, clearSelectedSeason, setViewMode } from '../store/contextSlice';
import { AllTimePlayer, AllTimeRoster, Franchise } from '../types/fantasy';
import './AllGibson.css';

type RosterGroup = keyof AllTimeRoster;

interface SlotDefinition {
  key: string;
  label: string;
  group: RosterGroup;
  position: 'qb' | 'rb' | 'wr' | 'te';
  index: number;
}

const BENCH_SLOT_COUNT = 8;
const MOBILE_BREAKPOINT = 799;

const LINEUP_SLOTS: SlotDefinition[] = [
  { key: 'qb1', label: 'QB1', group: 'Quarterbacks', position: 'qb', index: 0 },
  { key: 'qb2', label: 'QB2', group: 'Quarterbacks', position: 'qb', index: 1 },
  { key: 'rb1', label: 'RB1', group: 'RunningBacks', position: 'rb', index: 0 },
  { key: 'rb2', label: 'RB2', group: 'RunningBacks', position: 'rb', index: 1 },
  { key: 'wr1', label: 'WR1', group: 'WideReceivers', position: 'wr', index: 0 },
  { key: 'wr2', label: 'WR2', group: 'WideReceivers', position: 'wr', index: 1 },
  { key: 'wr3', label: 'WR3', group: 'WideReceivers', position: 'wr', index: 2 },
  { key: 'te1', label: 'TE', group: 'TightEnds', position: 'te', index: 0 },
];

const getPlayer = (franchise: Franchise, slot: SlotDefinition) =>
  franchise.AllTimeRoster?.[slot.group]?.[slot.index];

const getAllPlayers = (franchise: Franchise) => {
  const roster = franchise.AllTimeRoster;
  if (!roster) {
    return [];
  }

  return [
    ...roster.Quarterbacks,
    ...roster.RunningBacks,
    ...roster.WideReceivers,
    ...roster.TightEnds,
  ];
};

const getStarterPpg = (franchise: Franchise) => getAllPlayers(franchise)
  .filter((player) => !player.IsBench)
  .reduce((total, player) => total + player.AveragePoints, 0);

const getBenchPlayers = (franchise: Franchise) => getAllPlayers(franchise)
  .filter((player) => player.IsBench)
  .sort((first, second) => (
    second.AveragePoints - first.AveragePoints
    || second.TotalPoints - first.TotalPoints
    || second.WeeksStarted - first.WeeksStarted
    || first.PlayerName.localeCompare(second.PlayerName)
  ));

const getChampionships = (franchise: Franchise) =>
  franchise.Teams.filter((team) => team.Champion).length;

const formatPlayerSeasons = (player: AllTimePlayer) => {
  const seasons = [...player.SeasonsWithFranchise].sort((a, b) => a - b);
  return seasons.length > 1
    ? `'${seasons[0].toString().slice(-2)}–'${seasons[seasons.length - 1].toString().slice(-2)}`
    : seasons.length === 1
      ? `'${seasons[0].toString().slice(-2)}`
      : '';
};

const formatPlayerStats = (player: AllTimePlayer) => [
  `${Math.round(player.TotalPoints).toLocaleString()} pts`,
  `${player.WeeksStarted} starts`,
].join(' · ');

const formatPlayerMeta = (player: AllTimePlayer) =>
  [formatPlayerStats(player), formatPlayerSeasons(player)].filter(Boolean).join(' · ');

const getHeadshotThumbnailUrl = (headshotUrl: string) => headshotUrl.replace(
  /\/image\/(upload|private)\/(?:f_auto,q_auto\/)?/,
  '/image/$1/f_auto,q_auto,w_64,h_64,c_fill/',
);

const useMobileLayout = () => {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);

  useEffect(() => {
    const updateLayout = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, []);

  return isMobile;
};

const PlayerHeadshot: React.FC<{ player: AllTimePlayer }> = ({ player }) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [player.HeadshotUrl]);

  const initials = player.PlayerName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('');

  return (
    <span className="all-gibson-headshot" aria-hidden="true">
      {player.HeadshotUrl && !failed ? (
        <img
          src={getHeadshotThumbnailUrl(player.HeadshotUrl)}
          alt=""
          loading="lazy"
          decoding="async"
          fetchPriority="low"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      ) : (
        <span>{initials || player.Position}</span>
      )}
    </span>
  );
};

const PlayerCell: React.FC<{
  player?: AllTimePlayer;
  side: 'left' | 'right';
}> = ({ player, side }) => {
  if (!player) {
    return <div className={`all-gibson-player all-gibson-player--${side} is-empty`}>—</div>;
  }

  const meta = formatPlayerMeta(player);
  const stats = formatPlayerStats(player);
  const seasons = formatPlayerSeasons(player);
  const ageLabel = player.Age ? `, age ${player.Age}` : '';

  return (
    <div
      className={`all-gibson-player all-gibson-player--${side} ${player.IsCurrentRoster ? 'is-current-roster' : ''}`}
      title={`${player.PlayerName}: ${player.AveragePoints.toFixed(1)} PPG, ${meta}${ageLabel}${player.IsCurrentRoster ? ', current roster' : ''}`}
    >
      <div className="all-gibson-player-content">
        <PlayerHeadshot player={player} />
        <div className="all-gibson-player-copy">
          <div className="all-gibson-player-main">
            <strong>{player.PlayerName}</strong>
            <span>{player.AveragePoints.toFixed(1)}</span>
          </div>
          <div className="all-gibson-player-meta">
            {stats}
            {seasons && <span className="all-gibson-player-meta-seasons"> · {seasons}</span>}
          </div>
          {seasons && <div className="all-gibson-player-seasons">{seasons}</div>}
        </div>
      </div>
    </div>
  );
};

const TeamHeader: React.FC<{
  franchise: Franchise;
  side: 'left' | 'right';
}> = ({ franchise, side }) => (
  <div className={`all-gibson-team all-gibson-team--${side}`}>
    <FranchiseLogo
      franchiseId={franchise.Id}
      franchiseName={franchise.Name}
      size="medium"
      className="rounded"
    />
    <div className="all-gibson-team-copy">
      <strong title={franchise.Name}>{franchise.Name}</strong>
      <span>{franchise.Owner}</span>
    </div>
    <div className="all-gibson-team-score">
      <strong>{getStarterPpg(franchise).toFixed(1)}</strong>
      <span>starter PPG</span>
    </div>
    <div className="all-gibson-team-titles" title={`${getChampionships(franchise)} championships`}>
      <TrophyOutlined /> {getChampionships(franchise)}
    </div>
  </div>
);

const MatchupCard: React.FC<{
  left: Franchise;
  right: Franchise;
}> = ({ left, right }) => {
  const style = {
    '--left-team-color': left.Color || '#1a2b4c',
    '--right-team-color': right.Color || '#1a2b4c',
  } as CSSProperties;
  const leftBench = getBenchPlayers(left);
  const rightBench = getBenchPlayers(right);

  return (
    <article className="all-gibson-matchup" style={style}>
      <header className="all-gibson-matchup-header">
        <TeamHeader franchise={left} side="left" />
        <span className="all-gibson-versus" aria-hidden="true">VS</span>
        <TeamHeader franchise={right} side="right" />
      </header>

      <div className="all-gibson-lineup" role="table" aria-label={`${left.Name} versus ${right.Name} all-time rosters`}>
        {LINEUP_SLOTS.map((slot) => (
          <div
            className="all-gibson-lineup-row"
            role="row"
            key={slot.key}
          >
            <PlayerCell player={getPlayer(left, slot)} side="left" />
            <div className="all-gibson-position" role="rowheader">{slot.label}</div>
            <PlayerCell player={getPlayer(right, slot)} side="right" />
          </div>
        ))}
        <div className="all-gibson-lineup-divider" role="row">
          <span role="cell">Bench</span>
        </div>
        {Array.from({ length: BENCH_SLOT_COUNT }, (_, index) => (
          <div className="all-gibson-lineup-row is-bench" role="row" key={`bench-${index}`}>
            <PlayerCell player={leftBench[index]} side="left" />
            <div className="all-gibson-position" role="rowheader">BN</div>
            <PlayerCell player={rightBench[index]} side="right" />
          </div>
        ))}
      </div>
    </article>
  );
};

const AllGibson: React.FC = () => {
  const { data: franchises, isLoading, error } = useFranchises();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useMobileLayout();

  useEffect(() => {
    dispatch(clearSelectedFranchise());
    dispatch(clearSelectedSeason());
    dispatch(setViewMode('global'));
  }, [dispatch]);

  const rosterFranchises = useMemo(
    () => (franchises || [])
      .filter((franchise) => franchise.AllTimeRoster)
      .sort((first, second) => {
        const ppgDifference = getStarterPpg(second) - getStarterPpg(first);
        return ppgDifference || first.Name.localeCompare(second.Name);
      }),
    [franchises],
  );

  const requestedLeftId = searchParams.get('left');
  const requestedRightId = searchParams.get('right');
  const franchiseIds = useMemo(
    () => new Set(rosterFranchises.map((franchise) => franchise.Id)),
    [rosterFranchises],
  );
  const leftId = requestedLeftId && franchiseIds.has(requestedLeftId)
    ? requestedLeftId
    : rosterFranchises[0]?.Id || '';
  const rightId = requestedRightId
    && franchiseIds.has(requestedRightId)
    && requestedRightId !== leftId
    ? requestedRightId
    : rosterFranchises.find((franchise) => franchise.Id !== leftId)?.Id || '';

  useEffect(() => {
    if (!leftId || !rightId) {
      return;
    }

    if (requestedLeftId === leftId && requestedRightId === rightId) {
      return;
    }

    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('left', leftId);
      next.set('right', rightId);
      return next;
    }, { replace: true });
  }, [leftId, requestedLeftId, requestedRightId, rightId, setSearchParams]);

  const pairs = useMemo(() => {
    const result: Array<[Franchise, Franchise]> = [];
    for (let index = 0; index < rosterFranchises.length - 1; index += 2) {
      result.push([rosterFranchises[index], rosterFranchises[index + 1]]);
    }
    return result;
  }, [rosterFranchises]);

  const left = rosterFranchises.find((franchise) => franchise.Id === leftId);
  const right = rosterFranchises.find((franchise) => franchise.Id === rightId);

  const setComparison = (nextLeftId: string, nextRightId: string) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('left', nextLeftId);
      next.set('right', nextRightId);
      return next;
    });
  };

  const updateTeam = (side: 'left' | 'right', nextId: string) => {
    if (side === 'left') {
      if (nextId === rightId) {
        setComparison(nextId, leftId);
      } else {
        setComparison(nextId, rightId);
      }
      return;
    }

    if (nextId === leftId) {
      setComparison(rightId, nextId);
    } else {
      setComparison(leftId, nextId);
    }
  };

  const swapTeams = () => {
    setComparison(rightId, leftId);
  };

  if (isLoading) {
    return (
      <div className="all-gibson-page">
        <LoadingSkeleton height="100px" />
        <LoadingSkeleton height="640px" />
      </div>
    );
  }

  if (error || rosterFranchises.length < 2) {
    return (
      <div className="all-gibson-page">
        <EmptyState
          message="All-time rosters are unavailable"
          description="The franchise archive could not be loaded. Please try refreshing the page."
        />
      </div>
    );
  }

  return (
    <div className="all-gibson-page">
      <header className="all-gibson-hero">
        <div className="all-gibson-eyebrow"><CrownOutlined /> Gibsons League dream lineups</div>
        <h1>All Gibson</h1>
        <p>
          Every franchise's top historical starters, lined up like a fantasy matchup.
          Points and starts count only the player's regular-season starts for that franchise.
        </p>
      </header>

      {isMobile ? (
        <section className="all-gibson-mobile" aria-label="Choose two franchises to compare">
          <div className="all-gibson-picker">
            <label>
              <span>Left team</span>
              <select value={leftId} onChange={(event) => updateTeam('left', event.target.value)}>
                {rosterFranchises.map((franchise) => (
                  <option value={franchise.Id} key={franchise.Id}>{franchise.Name}</option>
                ))}
              </select>
            </label>
            <button type="button" onClick={swapTeams} aria-label="Swap comparison teams">
              <SwapOutlined />
            </button>
            <label>
              <span>Right team</span>
              <select value={rightId} onChange={(event) => updateTeam('right', event.target.value)}>
                {rosterFranchises.map((franchise) => (
                  <option value={franchise.Id} key={franchise.Id}>{franchise.Name}</option>
                ))}
              </select>
            </label>
          </div>
          {left && right && <MatchupCard left={left} right={right} />}
        </section>
      ) : (
        <section className="all-gibson-desktop" aria-label="All franchise all-time roster matchups">
          {pairs.map(([first, second]) => (
            <MatchupCard left={first} right={second} key={`${first.Id}-${second.Id}`} />
          ))}
        </section>
      )}

      <footer className="all-gibson-attribution">
        Player identity, biographical data, and headshot links via{' '}
        <a href="https://github.com/nflverse/nflverse-data" target="_blank" rel="noreferrer">nflverse</a>.{' '}
        Images are served by <a href="https://www.nfl.com/" target="_blank" rel="noreferrer">NFL.com</a>
        {' '}and remain the property of their respective owners.
      </footer>
    </div>
  );
};

export default AllGibson;
